// MTO API service functions
import axios from 'axios';
import { 
  MTO_SOLVE_API, 
  MTO_ALGORITHMS_API
} from '../../../../config';

import { globalColorManager } from '../../utils/ColorManager';

const convertLocationsToGeoJson = (locations) => {
  return {
    type: 'FeatureCollection',
    features: locations.map(loc => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [loc.longitude, loc.latitude] // GeoJSON format is [longitude,latitude]
      },
      properties: {
        id: loc.id,
        name: loc.name || ''
      }
    }))
  };
};

// Format locations for API
const formatLocationsForApi = (locations) => {
  return locations.map(loc => ({
    id: loc.id,
    lat: loc.latitude,
    lng: loc.longitude,
    name: loc.name
  }));
};

// Format connections for API
const formatConnectionsForApi = (connections) => {
  if (!connections || connections.length === 0) {
    return [];
  }
  
  return connections.map(conn => ({
    id: conn.id,
    transport_mode: conn.transportMode || conn.transport_mode,
    start_point_id: conn.points?.[0]?.id || conn.start_point_id || conn.startPointId,
    end_point_id: conn.points?.[1]?.id || conn.end_point_id || conn.endPointId,
    duration: conn.duration,
    color: conn.color
  }));
};

const processResponse = (responseData, locations) => {
  const isVrp = responseData.geojson?.properties?.is_vrp || false;
  const vehiclePathData = responseData.vehicle_paths || [];
  const formattedVehiclePaths = [];
  
  if (isVrp && vehiclePathData.length > 0) {
    // Process multi-vehicle paths
    for (const vehicle of vehiclePathData) {
      const pathObjects = mapPathIdsToLocations(vehicle.path, locations);
      
      if (pathObjects.length > 0) {
        formattedVehiclePaths.push({
          id: `vehicle_${vehicle.vehicle_id}`,
          vehicle_id: vehicle.vehicle_id,
          path: pathObjects,
          color: vehicle.color || getVehicleColor(vehicle.vehicle_id, vehicle),
          distance: vehicle.distance || 0,
          // Include all metadata from backend
          segments: vehicle.segments,
          demand: vehicle.demand,
          single_trip_duration: vehicle.single_trip_duration,
          trips_needed: vehicle.trips_needed,
          total_duration: vehicle.total_duration
        });
      }
    }
  } else if (responseData.path && responseData.path.length > 0) {
    // Fallback to single vehicle (backward compatibility)
    const pathObjects = mapPathIdsToLocations(responseData.path, locations);
    
    if (pathObjects.length > 0) {
      formattedVehiclePaths.push({
        id: 'vehicle_1',
        vehicle_id: 1,
        path: pathObjects,
        color: getVehicleColor(1, { distance: responseData.distance }),
        distance: responseData.distance
      });
    }
  }
  
  // Ensure path property is always a valid array
  const path = Array.isArray(responseData.path) ? responseData.path : [];
  if (!Array.isArray(responseData.path)) {
    console.warn('Got weird path data back from API:', responseData.path);
  }
  
  // Extract demand from vehicle_paths metadata if available
  const demand = responseData.vehicle_paths && responseData.vehicle_paths.length > 0 
    ? responseData.vehicle_paths[0].demand 
    : 0;

  return {
    path,
    vehiclePaths: formattedVehiclePaths,
    isVrp,
    distance: responseData.distance,
    time: responseData.duration,
    algorithm: responseData.algorithm,
    vehicleCount: responseData.vehicle_count,
    maxCapacity: responseData.max_capacity,
    minVehiclesNeeded: responseData.min_vehicles_needed,
    geojson: responseData.geojson,
    // Capacity constraint fields
    demand: demand,
    trips_needed: responseData.trips_needed,
    single_trip_duration: responseData.single_trip_duration,
    bottleneck_capacity: responseData.bottleneck_capacity,
    bottleneck_segment: responseData.bottleneck_segment
  };
};

// Map IDs to location objects
const mapPathIdsToLocations = (pathIds, locations) => {
  if (!pathIds || !Array.isArray(pathIds)) {
    return [];
  }
  
  return pathIds
    .map(pointId => {
      const location = locations.find(loc => loc.id === pointId);
      return location || null;
    })
    .filter(Boolean); // Filter out null values
};

export const solveMto = async (groupId, locations, startId = null, endId = null, connections = [], algorithm = "capacitated_savings", useGeoJson = true, demand = 0, modeCapacities = {}) => {
  try {
    // Build request parameters
    const requestParams = buildRequest(groupId, locations, startId, endId, connections, algorithm, useGeoJson, demand, modeCapacities);
    
    // Call API
    const response = await callApi(requestParams);
    
    if (response.data.success) {
      // Process the response data
      return processResponse(response.data.data, locations);
    } else {
      throw new Error(response.data.error || 'Solve failed');
    }
  } catch (error) {
    console.error('Solve failed:', error);
    throw error;
  }
};

const buildRequest = (groupId, locations, startId, endId, connections, algorithm, useGeoJson, demand = 0, modeCapacities = {}) => {
  const formattedConnections = formatConnectionsForApi(connections);
  
  const requestParams = {
    group_id: groupId,
    params: {
      start_id: startId,
      end_id: endId,
      connections: formattedConnections,
      algorithm: algorithm,
      demand: demand,
      mode_capacities: modeCapacities
    }
  };

  if (useGeoJson) {
    // Convert to GeoJSON format
    const locationsGeoJson = convertLocationsToGeoJson(locations);
    requestParams.params.locations_geojson = locationsGeoJson;

  } else {
    // Use traditional format
    const formattedLocations = formatLocationsForApi(locations);
    requestParams.params.locations = formattedLocations;
  }
  
  return requestParams;
};

const callApi = async (requestParams) => {
  try {
    return await axios.post(MTO_SOLVE_API, requestParams);
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

const getVehicleColor = (vehicleId, vehicleData = {}) => {
  return globalColorManager.getVehicleColor(vehicleId, vehicleData);
};

export const getMtoAlgorithms = async () => {
  try {
    const response = await axios.get(MTO_ALGORITHMS_API);
    
    if (response.data.success) {
      return response.data.data;
    } else {
      console.error('Algorithm fetch failed:', response.data.error);
      throw new Error(response.data.error || 'Failed to get algorithms');
    }
  } catch (error) {
    console.error('Algorithm API failed:', error);
    throw error;
  }
}; 